import { CircularProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import AdminUser from 'AdminUser';
import BackendService from 'BackendService';
import { debounce, DebouncedFunc } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette } from 'Palette';
import { StyledInput } from '../styles/StyledInputs';
import { FormContainer, StyledHeading } from '../styles/styles';

interface ToneProps {
  tone: keyof typeof Palette;
}

interface AdminUserSearchBarProps extends ToneProps {
  onResultClick: (id: string) => void;
}

interface State {
  query: string;
  results: AdminUser[];
  loading: boolean;
  selectedIndex: number;
  showResults: boolean;
}

const AdminUserSearchBar = (props: ToneProps) => {
  const navigate = useNavigate();

  return (
    <AdminUserSearchBarImpl
      tone={props.tone}
      onResultClick={(id) => navigate(`/adminUsers/${id}`)}
    />
  );
};

class AdminUserSearchBarImpl extends React.Component<AdminUserSearchBarProps, State> {
  private searchRef = React.createRef<HTMLDivElement>();
  private inputRef = React.createRef<HTMLInputElement>();
  debouncedSearch: DebouncedFunc<(query: string) => void>;

  constructor(props: AdminUserSearchBarProps) {
    super(props);
    this.state = {
      query: '',
      results: [],
      loading: false,
      selectedIndex: -1,
      showResults: false,
    };

    this.debouncedSearch = debounce(this.performSearch.bind(this), 300);
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.searchRef.current && !this.searchRef.current.contains(event.target as Node)) {
      this.hideResults();
    }
  };

  hideResults = () => {
    this.setState({
      showResults: false,
      selectedIndex: -1,
    });
  };

  performSearch(query: string) {
    if (query.trim().length < 2) {
      this.setState({
        results: [],
        loading: false,
        showResults: false,
        selectedIndex: -1,
      });
      return;
    }

    this.setState({ loading: true });
    new BackendService().searchAdminUser(query).then((results) => {
      this.setState({
        results,
        loading: false,
        showResults: true,
        selectedIndex: -1,
      });
    });
  }

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.setState({ query });
    this.debouncedSearch(query);
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { results, selectedIndex, showResults, loading } = this.state;

    if (!showResults || results.length === 0 || loading) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.setState({
          selectedIndex: selectedIndex < results.length - 1 ? selectedIndex + 1 : 0,
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.setState({
          selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : results.length - 1,
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          this.handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.hideResults();
        if (this.inputRef.current) {
          this.inputRef.current.blur();
        }
        break;
    }
  };

  handleResultClick = (adminUser: AdminUser) => {
    this.setState(
      {
        results: [],
        query: '',
        showResults: false,
        selectedIndex: -1,
      },
      () => {
        this.props.onResultClick(adminUser.id!);
      },
    );
  };

  handleClick = (id: string) => () => {
    this.props.onResultClick(id);
  };

  render() {
    const { query, results, loading, selectedIndex, showResults } = this.state;
    const tone = this.props.tone || 'default';

    return (
      <FormContainer>
        <div ref={this.searchRef}>
          <StyledHeading tone={tone}>Search Admin Users</StyledHeading>
          <StyledInput
            ref={this.inputRef}
            fullWidth
            variant="outlined"
            placeholder="Search admin users..."
            value={query}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            size="small"
          />

          {loading && <CircularProgress size={20} style={{ marginTop: 8 }} />}

          {!loading && showResults && results.length > 0 && (
            <List>
              {results.map((user, idx) => (
                <ListItem
                  key={user.id}
                  onClick={() => this.handleResultClick(user)}
                  component="button"
                  disableGutters
                  sx={{
                    textAlign: 'left',
                    border:
                      idx === selectedIndex
                        ? '2px dotted rgba(0, 0, 0, 0.4)'
                        : '2px solid transparent',
                    borderRadius: 1,
                    p: 1,
                    transition: 'border-color 0.1s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      border: '2px dotted rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <ListItemText
                    primary={`${user.firstName || ''} ${user.lastName || ''}`}
                    secondary={user.email || 'No email'}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              No results found.
            </Typography>
          )}
        </div>
      </FormContainer>
    );
  }
}

export default AdminUserSearchBar;
